import { defineComponent as _defineComponent } from 'vue'
import * as validation from 'JS/components/blain-form/validation';
import BForm from 'JS/components/blain-form/BForm.vue';
import bugsnagClient from 'JS/global/bugsnag';
import ErrorBanner from 'JS/components/ErrorBanner.vue';
import LinkButton from 'JS/components/LinkButton.vue';
import { BButtonSubmitButton, BTextField } from "Src/js/components/blain-form";
import { CreateOauthAccountRequest, IAuthenticateError, IAuthenticateResponse, ICreateAccountResource, ICreateAccountResponse, LoginMethod } from 'JS/types/Account';
import { onMounted, ref } from 'vue';
import { FormatOptions } from 'JS/components/blain-form/formatting';
import { IJoinRewardsStep, ILinkRewardsUser, ILinkRewardsUserResponse, RewardsReasonCode } from 'JS/types/rewards';
import { useServices } from '../../composables/services';


export default /*#__PURE__*/_defineComponent({
  __name: 'EnterVerificationCode',
  props: {
    redactedString: null,
    userPhone: null,
    userEmail: null,
    rewardsSessionId: null,
    createAccountRequest: null,
    createOauthAccountRequest: null,
    antiForgeryToken: null
  },
  emits: ["close", "switch-page", "loading"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'close'): void,
	(e: 'switch-page', page: IJoinRewardsStep): void,
	(e: 'loading', value: boolean): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	redactedString?: string,
	userPhone?: string,
	userEmail?: string,
	rewardsSessionId?: string,
	createAccountRequest?: ICreateAccountResource,
	createOauthAccountRequest?: CreateOauthAccountRequest,
	antiForgeryToken: string,
}





const { rewardsService } = useServices();
const { accountService } = useServices();

const userInput = ref<string>("");
const errorMessage = ref<string>("");
const createAccountErrorMessage = ref<string>("");
const headerFocuser = ref<HTMLElement | null>(null);

const verificationCodeFormat: FormatOptions = {
	type: 'numeric',
	length: 6
};

const rewardsNumberFormat: FormatOptions = {
	type: 'numeric',
	length: 13
};

async function handleValidateConfirmationCode() {
	emit('loading', true);

	if (props.redactedString) {
		const result = await rewardsService.confirmUserCode(
			{ code: Number(userInput.value), rewardsSessionId: props.rewardsSessionId, createAccountRequest: props.createAccountRequest, createOauthAccountRequest: props.createOauthAccountRequest }
			, props.antiForgeryToken
		);

		if (!result?.success) {
			if (result.reasonCode === RewardsReasonCode.CUSTOMER_HAS_REWARDS) {
				emit('switch-page', 'customer-has-rewards');
			} else {
				errorMessage.value = result.errorMessage;
			}
		} else {
			if (props.createAccountRequest || props.createOauthAccountRequest) {
				window.blainGtmDataLayer.push({
					'event': "create_login",
					'loginMethod': !!props.createAccountRequest ? LoginMethod.Local : props.createOauthAccountRequest.loginMethod
				});
			}
			if (result.createdRewardsDesync) {
				window.blainGtmDataLayer.push({
					'event': "rewards_link_email_mismatch"
				});
			}
			window.blainGtmDataLayer.push({
				'event': "connect_rewards"
			});
			emit('switch-page', 'success');
		}
	} else {
		const userRewardsData: ILinkRewardsUser = {
			phoneNumber: props.userPhone,
			email: props.userEmail,
			rewardsNumber: userInput.value,
			verificationMethod: 'rewardsNumber',
		};
		const results: ILinkRewardsUserResponse = await rewardsService.linkUserWithRewardsNum(userRewardsData, props.createAccountRequest, props.createOauthAccountRequest, props.antiForgeryToken);
		if (!results.success) {
			errorMessage.value = results.errorMessage;
		}  else {
			if (props.createAccountRequest || props.createOauthAccountRequest) {
				window.blainGtmDataLayer.push({
					'event': "create_login",
					'loginMethod': !!props.createAccountRequest ? LoginMethod.Local : props.createOauthAccountRequest.loginMethod
				});
			}
			if (results.createdRewardsDesync) {
				window.blainGtmDataLayer.push({
					'event': "rewards_link_email_mismatch"
				});
			}
			window.blainGtmDataLayer.push({
				'event': "connect_rewards"
			});
			emit('switch-page', 'success');
		}
	}
	emit('loading', false);
}

async function handleSkipConnectingRewards() {
	if (!props.createAccountRequest && !props.createOauthAccountRequest) {
		emit('close');
		return;
	}

	try {
		emit('loading', true);
		if (props.createAccountRequest) {
			await attemptCreateAccount();
		} else {
			await attemptCreateOauthAccount();
		}
	} catch (err) {
		createAccountErrorMessage.value = "Something went wrong. Please try again later.";
		emit('loading', false);
		bugsnagClient.notify(err);
	}
}

async function attemptCreateAccount() {
	const result: ICreateAccountResponse = await accountService.createAccount({ ...props.createAccountRequest, skipConnectingRewards: true } , props.antiForgeryToken);
	if (!result?.success) {
		createAccountErrorMessage.value = result.errorMessage;
		emit('loading', false);
	} else {
		window.blainGtmDataLayer.push({
			'event': "create_login",
			'loginMethod': LoginMethod.Local,
		});
		window.blainGtmDataLayer.push({
			'event': "skipped_connecting_rewards",
			'loginMethod': LoginMethod.Local,
		});
		if (result.headerJson && window.vueEventBus) {
			window.vueEventBus.$emit('header-vm', result.headerJson);
		}
		emit('close');
	}
}

async function attemptCreateOauthAccount() {
	let result: IAuthenticateResponse | IAuthenticateError = null;

	if (props.createOauthAccountRequest.loginMethod === LoginMethod.Apple) {
		result = await accountService.appleLogin(JSON.parse(props.createOauthAccountRequest.credentialToken), props.createOauthAccountRequest.skipCartMerge, true);
	} else if (props.createOauthAccountRequest.loginMethod === LoginMethod.Facebook) {
		result = await accountService.facebookLogin(JSON.parse(props.createOauthAccountRequest.credentialToken), props.createOauthAccountRequest.skipCartMerge, true);
	} else {
		result = await accountService.googleLogin(props.createOauthAccountRequest.credentialToken, props.createOauthAccountRequest.skipCartMerge, true);
	}

	if (!result?.success) {
		if ('error' in result) {
			const errorMessage = `${result.error}`
		+ (result.message ? ` - ${result.message}` : '');
			createAccountErrorMessage.value =
		result.error
			? errorMessage
			: "Something went wrong. Please try again later.";
		}

		emit('loading', false);
	} else {
		window.blainGtmDataLayer.push({
			'event': "create_login",
			'loginMethod': props.createOauthAccountRequest.loginMethod,
		});
		window.blainGtmDataLayer.push({
			'event': "skipped_connecting_rewards",
			'loginMethod': props.createOauthAccountRequest.loginMethod,
		});
		emit('close');
	}
}

onMounted(()=> {
	headerFocuser.value?.focus();
});


return { __sfc: true,props, emit, rewardsService, accountService, userInput, errorMessage, createAccountErrorMessage, headerFocuser, verificationCodeFormat, rewardsNumberFormat, handleValidateConfirmationCode, handleSkipConnectingRewards, attemptCreateAccount, attemptCreateOauthAccount, validation, BForm, ErrorBanner, LinkButton, BButtonSubmitButton, BTextField }
}

})